// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	AllocationPayload,
	Category,
	CategoryPayload,
	ErrorCause,
	ExchangeRateByDate,
	Feedback,
	Loan,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Allocations } from '../../fields/Allocations/Allocations';

// ui
import { Loading } from '../../ui/Loading/Loading';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface FormEditCategoryValues {
	name: string;
	allocations: AllocationPayload[];
	exchangeRates: ExchangeRateByDate[];
}

export interface FormEditCategoryProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditCategory = ({ onClose, onSuccess, setIsLoading }: FormEditCategoryProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// state
	const [category, setCategory] = useState<Category | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState<FormEditCategoryValues>({
		name: '',
		allocations: [],
		exchangeRates: [],
	});

	// params
	const paramCategory = searchParams.get('dialogCategory');

	// vars
	const idForm = 'form-edit-category';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch category
	const { isLoading: isLoadingCategory } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/categories/${paramCategory}?populate=allocations.conversions,allocations.loan`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setCategory(res.data[0]);
			}
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear feedback
			setFeedback(null);

			// Validate: check for duplicate loans
			const loansWithValues = values.allocations.filter((a) => a.loan !== null).map((a) => a.loan);
			const duplicateLoans = loansWithValues.filter(
				(loan, index) => loansWithValues.indexOf(loan) !== index
			);

			if (duplicateLoans.length > 0) {
				setFeedback({
					alert: {
						children: t('error.duplicateLoanAllocations'),
					},
				});
				return;
			}

			// set loading
			setIsLoading(true);

			if (!paramCategory) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const body: Partial<CategoryPayload> = {
				name: String(entries.name),
				allocations: values.allocations
					.filter((a) => a.amount && a.loan !== 0)
					.map((allocation) => ({
						amount: Number(allocation.amount),
						conversions: allocation.conversions,
						currency: allocation.currency,
						loan: Number(allocation.loan),
					})),
			};

			const fetchEditCategory = await fetch(
				`${process.env.REACT_APP_API_URL}/categories/${paramCategory}`,
				{
					method: 'PATCH',
					body: JSON.stringify(body),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			const resEditCategory = await fetchEditCategory.json();

			if (resEditCategory.error) {
				throw new Error(resEditCategory.error.message, {
					cause: {
						details: resEditCategory.error?.details,
						id: resEditCategory.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.categoryUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'CATEGORY_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.categoryNonExistent', { id: category?.id });
				} else if (cause.id === 'CATEGORY_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				} else if (cause.id === 'CATEGORY_DEFAULT_NOT_EDITABLE') {
					feedbackNew.alert.children = t('error.categoryDefaultNotEditable');
				} else if (cause.id === 'CATEGORY_ALLOCATION_INVALID_AMOUNT') {
					feedbackNew.alert.children = t('error.allocationInvalidAmount');
				} else if (cause.id === 'CATEGORY_ALLOCATION_CURRENCY_REQUIRED') {
					feedbackNew.alert.children = t('error.allocationCurrencyRequired');
				} else if (cause.id === 'CATEGORY_ALLOCATION_LOAN_NOT_FOUND') {
					feedbackNew.alert.children = t('error.allocationLoanNotFound');
				} else if (cause.id === 'CATEGORY_ALLOCATION_EXCEEDS_LOAN_AMOUNT') {
					feedbackNew.alert.children = t('error.allocationExceedsLoanAmount', {
						currency: cause.details.currency,
						loanTotal: formatCurrency({
							currency: cause.details.currency,
							value: cause.details.loan?.total || 0,
						}),
						total: formatCurrency({
							currency: cause.details.currency,
							value: cause.details.total || 0,
						}),
					});
				} else if (cause.id === 'PROJECT_REPORTING_CURRENCY_NOT_FOUND') {
					feedbackNew.alert.children = t('error.reportingCurrencyNotFound');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleChangeAllocations = (allocations: AllocationPayload[]) => {
		setValues({
			...values,
			allocations,
		});
	};

	const handleChangeExchangeRates = (exchangeRates: ExchangeRateByDate[]) => {
		setValues((prevValues) => {
			// Recalculate all allocation conversions with new exchange rates
			const updatedAllocations = prevValues.allocations.map((allocation) => ({
				...allocation,
				conversions: exchangeRates.map((rate) => ({
					amount:
						rate.exchangeRate > 0 ? Number((allocation.amount / rate.exchangeRate).toFixed(2)) : 0,
					currency: rate.currency,
					dateExchangeRate: rate.dateExchangeRate,
					exchangeRate: rate.exchangeRate,
					tax: allocation.conversions?.find((c) => c.currency === rate.currency)?.tax || 0,
				})),
			}));

			return {
				...prevValues,
				allocations: updatedAllocations,
				exchangeRates,
			};
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingCategory) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				{featureFlags['IFRS'] && (
					<Grid item xs={12}>
						<Allocations
							allocations={values.allocations}
							exchangeRates={values.exchangeRates}
							idForm={idForm}
							onChange={handleChangeAllocations}
							onChangeExchangeRates={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	// set default values when category changes
	useEffect(() => {
		if (category) {
			const valuesNew: Partial<FormEditCategoryValues> = {
				name: category.name,
			};

			if (category.allocations?.length) {
				// Transform CategoryAllocation[] to CategoryAllocationPayload[]
				valuesNew.allocations = category.allocations.map((allocation) => ({
					amount: allocation.amount,
					conversions: allocation.conversions?.map((conversion) => ({
						amount: conversion.amount,
						currency: conversion.currency,
						dateExchangeRate: conversion.dateExchangeRate
							? formatDate({ date: new Date(conversion.dateExchangeRate), format: 'YYYY-MM-DD' })
							: '',
						exchangeRate: conversion.exchangeRate,
						tax: conversion.tax,
					})),
					currency: allocation.currency,
					loan:
						typeof allocation.loan === 'object' ? (allocation.loan as Loan).id : allocation.loan,
				}));

				// Extract exchange rates from existing allocations
				const reportingCurrency =
					project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || '';
				const currenciesForExchangeRates = getCurrenciesForExchangeRates({
					currency: reportingCurrency,
				});

				valuesNew.exchangeRates = currenciesForExchangeRates.map((currency) => {
					const existingConversion = category.allocations
						?.flatMap((alloc) => alloc.conversions || [])
						.find((conv) => conv.currency === currency);

					return {
						currency,
						dateExchangeRate: existingConversion?.dateExchangeRate
							? formatDate({
									date: new Date(existingConversion.dateExchangeRate),
									format: 'YYYY-MM-DD',
								})
							: '',
						exchangeRate: existingConversion?.exchangeRate || 0,
					};
				});
			} else {
				// Initialize empty exchange rates from project currencies
				const reportingCurrency =
					project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || '';
				const currenciesForExchangeRates = getCurrenciesForExchangeRates({
					currency: reportingCurrency,
				});

				valuesNew.exchangeRates = currenciesForExchangeRates
					.map((currency) => ({
						currency,
						dateExchangeRate: '',
						exchangeRate: 0,
					}))
					.sort((a, b) => a.currency.localeCompare(b.currency));
			}

			setValues((valuesOld) => {
				return {
					...valuesOld,
					...valuesNew,
				};
			});
		}
	}, [category, getCurrenciesForExchangeRates, project?.currencies]);

	return (
		<form id={`${idForm}`} name="formEditCategory" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
